import { CalendarDate } from '@internationalized/date';
import { Production } from '__types__';
import { getEventDates } from 'v1/helpers/byModel/EventHelper';
import { dateConvert } from 'v5/platform/lang/dateConvert';

/**
 * Production-related logic utilities for callsheet creation
 */
export const productionLogic = {
  /**
   * Calculates the smart default callsheet date from production date ranges.
   *
   * Logic:
   * 1. Find the intersection of all production date ranges (common subrange)
   * 2. If intersection exists, use the earliest day (start of intersection)
   * 3. If no intersection (empty range), use max(end_date) of all productions
   *
   * @param productions - Array of production objects with production_date Event
   * @returns CalendarDate representing the smart default date, or null if no valid date can be determined
   */
  calculateSmartDefaultRange(productions: Production[]): CalendarDate | null {
    if (!productions || productions.length === 0) {
      return null;
    }

    // Extract date ranges from all productions as CalendarDate objects
    const dateRanges: Array<{ start: CalendarDate; end: CalendarDate }> = [];
    for (const production of productions) {
      if (!production?.production_date) {
        continue;
      }

      const [start, end] = getEventDates(production.production_date);

      const startCalendarDate = dateConvert.calendarDate.from(start);
      const endCalendarDate = dateConvert.calendarDate.from(end);

      if (startCalendarDate && endCalendarDate) {
        dateRanges.push({
          start: startCalendarDate,
          end: endCalendarDate
        });
      }
    }

    if (dateRanges.length === 0) {
      return null;
    }

    // Find the intersection: max(start_dates) to min(end_dates)
    // Using CalendarDate.compare() for comparisons
    const maxStart = dateRanges.reduce((max, range) => {
      return range.start.compare(max.start) > 0 ? range : max;
    }, dateRanges[0]);

    const minEnd = dateRanges.reduce((min, range) => {
      return range.end.compare(min.end) < 0 ? range : min;
    }, dateRanges[0]);

    // Check if intersection exists (maxStart.start <= minEnd.end)
    if (maxStart.start.compare(minEnd.end) <= 0) {
      // Intersection exists - use the earliest day of intersection (maxStart.start)
      return maxStart.start;
    } else {
      // No intersection - use max(end_date) of all productions
      const maxEnd = dateRanges.reduce((max, range) => {
        return range.end.compare(max.end) > 0 ? range : max;
      }, dateRanges[0]);
      return maxEnd.end;
    }
  }
};
